// F-111 : offre, consommation, changement d'offre, paiement simulé, abonnements et factures.
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { useSession } from "@core/session";
import { Badge, Button, Card, Empty, ErrorBox, KeyValues, Loading, Modal, Notice, PageHeader, Table, useToast } from "@core/ui";
import { Gauge, Mode, PLAN_CADENCE, useAction } from "./shared";

type Plan = { code: string; name: string; audience: string; price_cents: number; price_is_ttc: boolean; cadence: string; limits: Record<string, number>; hypothesis: boolean };
type UsageRow = { metric: string; label: string; used: number; limit: number | null; period: string; note: string };

const LIMIT_LABELS: Record<string, string> = {
  logements: "logements",
  campagnes_actives: "campagnes ouvertes",
  analyses_par_campagne: "analyses par campagne",
  signatures_par_an: "signatures par an",
  duree_jours: "jours d’accès",
  banques: "banques",
  utilisateurs: "membres",
};

const SUB_STATUS: Record<string, string> = { active: "Active", impayee: "Échéance impayée", terminee: "Terminée", annulee: "Annulée" };
const SESSION_STATUS: Record<string, string> = { ouverte: "En attente de paiement", payee: "Payée", echouee: "Paiement refusé", expiree: "Expirée", annulee: "Annulée" };

function price(p: Plan) {
  if (p.cadence === "gratuit") return "Gratuit";
  return `${money(p.price_cents)} ${p.price_is_ttc ? "TTC" : "HT"} ${PLAN_CADENCE[p.cadence] ?? ""}`;
}

export function UsageList({ usage }: { usage: UsageRow[] }) {
  return (
    <div className="stack-sm">
      {usage.map((u) => (
        <Gauge key={u.metric} label={u.label} used={u.used} limit={u.limit} note={u.note || undefined} />
      ))}
    </div>
  );
}

export function BillingPage() {
  const org = useOrg();
  const toast = useToast();
  const nav = useNavigate();
  const invalidate = useInvalidate();
  const { refresh } = useSession();
  const q = useGet<any>(org.api("/billing"));
  const action = useAction();
  const [confirm, setConfirm] = useState<Plan | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const current: Plan = d.plan;
  const choose = async (p: Plan) => {
    const r = await action.run(() => post<any>(org.api("/billing/checkout"), { plan_code: p.code }));
    setConfirm(null);
    if (!r) return;
    if (r.changed) {
      setWarnings(r.warnings ?? []);
      toast(`Offre « ${p.name} » appliquée.`);
      await refresh();
      invalidate(org.api("/billing"));
    } else {
      nav(org.to(`abonnement/paiement/${r.session_id}`));
    }
  };
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Abonnement" intro="Votre offre, ce que vous consommez et vos factures. Les tarifs affichés sont des hypothèses en cours de validation." actions={<Mode mode={d.gateway?.mode} />} />
      <Notice tone="simulateur" title="Paiement simulé">
        Aucun prestataire de paiement n’est encore choisi : les paiements et factures de cette page sont simulés, sans valeur comptable. Aucune carte n’est demandée.
      </Notice>
      {warnings.length ? (
        <Notice tone="attention" title="Votre usage dépasse les limites de la nouvelle offre">
          <ul className="list-plain">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
      <div className="grid-2">
        <Card title="Offre actuelle" corne>
          <div className="stack-sm">
            <p className="row">
              <strong>{current.name}</strong>
              {current.hypothesis ? <Badge tone="attention">Tarif à valider</Badge> : null}
            </p>
            <p className="plateforme-prix">{price(current)}</p>
            <p className="muted small">{current.audience}</p>
          </div>
        </Card>
        <Card title="Consommation">
          <UsageList usage={d.usage} />
        </Card>
      </div>
      <Card title="Changer d’offre">
        <ErrorBox error={action.error} />
        {!d.can_manage ? <Notice>Seul un propriétaire de l’espace peut changer d’offre.</Notice> : null}
        <div className="plateforme-offres">
          {(d.plans as Plan[]).map((p) => (
            <div key={p.code} className={`plateforme-offre ${p.code === current.code ? "plateforme-offre-actuelle" : ""}`}>
              <strong>{p.name}</strong>
              <span className="muted small">{p.audience}</span>
              <span className="plateforme-prix">{price(p)}</span>
              <ul className="plateforme-etapes">
                {Object.entries(p.limits).map(([k, v]) => (
                  <li key={k}>
                    {v} {LIMIT_LABELS[k] ?? k}
                  </li>
                ))}
              </ul>
              {p.hypothesis ? <Badge tone="attention">Hypothèse non validée</Badge> : null}
              {p.code === current.code ? (
                <Badge tone="encre">Offre actuelle</Badge>
              ) : d.can_manage ? (
                <Button variant="secondaire" small busy={action.busy} onClick={() => setConfirm(p)}>
                  {p.cadence === "campagne" ? "Acheter une campagne" : p.cadence === "gratuit" ? "Revenir à cette offre" : "Choisir cette offre"}
                </Button>
              ) : null}
            </div>
          ))}
        </div>
        <ul className="list-plain small muted">
          {(d.notes as string[]).map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      </Card>
      {d.can_manage ? <BillingHistory d={d} /> : null}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm ? `Passer à l’offre « ${confirm.name} »` : ""}
        footer={
          <>
            <Button variant="discret" onClick={() => setConfirm(null)}>
              Annuler
            </Button>
            <Button busy={action.busy} onClick={() => confirm && choose(confirm)}>
              {confirm?.cadence === "gratuit" ? "Confirmer le changement" : "Continuer vers le paiement"}
            </Button>
          </>
        }
      >
        {confirm ? (
          <div className="stack-sm">
            <p>
              Prix : <strong>{price(confirm)}</strong> (hypothèse tarifaire non validée).
            </p>
            {confirm.cadence === "gratuit" ? (
              <p>Le changement est immédiat. Rien n’est supprimé : si votre usage dépasse les limites, seules les nouvelles créations seront bloquées.</p>
            ) : (
              <p>Vous serez dirigé vers une page de paiement simulée. L’offre ne change qu’après confirmation du paiement.</p>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function BillingHistory({ d }: { d: any }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const action = useAction();
  const active = (d.subscriptions ?? []).find((s: any) => s.kind === "abonnement" && (s.status === "active" || s.status === "impayee"));
  const simulate = async (outcome: string) => {
    const r = await action.run(() => post<any>(org.api("/billing/subscription/simulate"), { outcome }));
    if (r) {
      toast(r.resultat ?? "Événement simulé.");
      invalidate(org.api("/billing"));
    }
  };
  return (
    <div className="grid-2">
      <Card
        title="Abonnements et campagnes"
        actions={
          active ? (
            <>
              <Button variant="discret" small busy={action.busy} onClick={() => simulate("renouvellement")}>
                Simuler un renouvellement
              </Button>
              <Button variant="discret" small busy={action.busy} onClick={() => simulate("impaye")}>
                Simuler une échéance impayée
              </Button>
            </>
          ) : null
        }
      >
        <ErrorBox error={action.error} />
        {(d.subscriptions ?? []).length === 0 ? (
          <Empty title="Aucun abonnement.">Vous utilisez l’offre gratuite ou une offre attribuée à l’espace.</Empty>
        ) : (
          <Table caption="Abonnements">
            <thead>
              <tr>
                <th>Offre</th>
                <th>Période</th>
                <th>État</th>
                <th className="right">Montant</th>
              </tr>
            </thead>
            <tbody>
              {d.subscriptions.map((s: any) => (
                <tr key={s.id}>
                  <td>
                    {s.plan_code} <Mode mode={s.mode === "simulateur" ? "simulator" : s.mode} />
                  </td>
                  <td>
                    {date(s.period_start)} – {date(s.period_end)}
                  </td>
                  <td>{SUB_STATUS[s.status] ?? s.status}</td>
                  <td className="right num">{money(s.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card title="Factures">
        {(d.invoices ?? []).length === 0 ? (
          <Empty title="Aucune facture." />
        ) : (
          <Table caption="Factures">
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Date</th>
                <th className="right">TTC</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.invoices.map((i: any) => (
                <tr key={i.id}>
                  <td>
                    <span className="mono">{i.number}</span>
                    <span className="block small muted">{i.label}</span>
                  </td>
                  <td>{date(i.issued_on)}</td>
                  <td className="right num">{money(i.amount_ttc_cents)}</td>
                  <td className="right">
                    <a href={org.api(`/billing/invoices/${i.id}/pdf`)} target="_blank" rel="noreferrer">
                      PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {(d.sessions ?? []).length ? (
          <details>
            <summary className="small">Tentatives de paiement</summary>
            <ul className="list-plain small">
              {d.sessions.map((s: any) => (
                <li key={s.id}>
                  {dateTime(s.created_at)} · {s.plan_code} · {money(s.amount_cents)} · {SESSION_STATUS[s.status] ?? s.status}
                  {s.failure_reason ? ` (${s.failure_reason})` : ""}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </Card>
    </div>
  );
}

/** Page de paiement du simulateur : choisir l'issue, traitée comme un événement signé du prestataire. */
export function CheckoutPage() {
  const org = useOrg();
  const { sessionId = "" } = useParams();
  const toast = useToast();
  const nav = useNavigate();
  const { refresh } = useSession();
  const invalidate = useInvalidate();
  const q = useGet<any>(org.api(`/billing/checkout/${sessionId}`));
  const action = useAction();
  const [result, setResult] = useState<string | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const s = q.data.session;
  const p: Plan | null = q.data.plan;
  const pay = async (outcome: "succes" | "echec") => {
    const r = await action.run(() => post<any>(org.api(`/billing/checkout/${sessionId}/simulate`), { outcome }));
    if (!r) return;
    setResult(r.resultat);
    await refresh();
    invalidate(org.api("/billing"));
    q.refetch();
    if (outcome === "succes") toast("Paiement simulé confirmé.");
  };
  return (
    <div className="page">
      <PageHeader eyebrow="Abonnement" title="Paiement simulé" intro="Cette page remplace celle d’un prestataire de paiement tant qu’aucun n’est choisi. Aucune donnée de carte n’est demandée." actions={<Mode mode="simulator" />} />
      <Card title={s.kind === "campagne" ? "Achat d’une campagne ponctuelle" : "Abonnement"}>
        <KeyValues
          items={[
            ["Offre", p?.name ?? s.plan_code],
            ["Montant", `${money(s.amount_cents)} ${s.price_is_ttc ? "TTC" : "HT"} (hypothèse)`],
            ["État", SESSION_STATUS[s.status] ?? s.status],
            ["Valable jusqu’au", dateTime(s.expires_at)],
          ]}
        />
        <ErrorBox error={action.error} />
        {result ? <Notice tone={s.status === "payee" ? "succes" : "info"}>{result}</Notice> : null}
        {s.status === "ouverte" ? (
          <div className="row">
            <Button busy={action.busy} onClick={() => pay("succes")}>
              Simuler un paiement accepté
            </Button>
            <Button variant="secondaire" busy={action.busy} onClick={() => pay("echec")}>
              Simuler un paiement refusé
            </Button>
          </div>
        ) : (
          <p>
            <Link to={org.to("abonnement")}>Revenir à l’abonnement</Link>
          </p>
        )}
        <p className="small muted">
          Le simulateur envoie un événement signé au même point d’entrée que le futur prestataire : l’offre ne change qu’à réception d’un paiement confirmé, et un événement rejoué ne refacture pas.
        </p>
      </Card>
      <p>
        <Button variant="discret" onClick={() => nav(org.to("abonnement"))}>
          Retour
        </Button>
      </p>
    </div>
  );
}

/** Bloc du tableau de bord : consommation de l'offre. */
export function UsageDashboard({ orgId }: { orgId: string }) {
  const q = useGet<any>(`/api/v1/orgs/${orgId}/billing/usage`);
  if (!q.data) return null;
  const plan: Plan = q.data.plan;
  const rows: UsageRow[] = (q.data.usage as UsageRow[]).filter((u) => u.limit !== null || u.used > 0);
  return (
    <Card title="Votre offre" actions={<Link to={`/espace/${orgId}/abonnement`}>Abonnement</Link>}>
      <div className="stack-sm">
        <p className="row">
          <strong>{plan.name}</strong>
          {plan.hypothesis ? <Badge tone="attention">Tarif à valider</Badge> : null}
        </p>
        <UsageList usage={rows} />
      </div>
    </Card>
  );
}
